import { useCallback } from 'react'
import {} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import { EvidenceServicePromiseClient } from '@trustero/trustero-api-web/lib/evidence/service_grpc_web_pb'
import {
  CreateRequestManualEvidenceRequest,
  CreateControlManualEvidenceRequest,
  CreateOrphanedManualEvidenceRequest,
  EvidencePayload,
} from '@trustero/trustero-api-web/lib/evidence/service_pb'

import { Timestamp } from 'google-protobuf/google/protobuf/timestamp_pb'
import { useInvalidateEvidence } from '../../../Utils/swrCacheInvalidation'
import { useAuthorizedGrpcClientWithContentUpdate } from '../../../adapter/grpcClient'
import { showInfoToast } from '../../../Utils/helpers/toast'

interface CreateEvidenceArgs {
  controlId?: string
  requestId?: string
}

interface CreateEvidenceCallbackArgs {
  mime: string
  bodyBytes: Uint8Array
  caption: string
  relevantDate: Timestamp
  fileName?: string
}

export const useCreateEvidence = ({
  controlId,
  requestId,
}: CreateEvidenceArgs): (({
  mime,
  bodyBytes,
  caption,
  relevantDate,
  fileName,
}: CreateEvidenceCallbackArgs) => Promise<string>) => {
  const invalidateEvidence = useInvalidateEvidence()
  const evidenceClient = useAuthorizedGrpcClientWithContentUpdate(
    EvidenceServicePromiseClient,
  )
  return useCallback(
    async ({
      mime,
      bodyBytes,
      caption,
      relevantDate,
      fileName,
    }: CreateEvidenceCallbackArgs) => {
      const controlIdString = controlId || ''
      const requestIdString = requestId || ''

      const newEvidence = new EvidencePayload()
        .setMime(mime)
        .setBody(bodyBytes)
        .setCaption(caption.trim())
        .setFileName(fileName?.trim() || '')
        .setRelevantDate(relevantDate)

      try {
        if (controlId) {
          await evidenceClient.createControlManualEvidence(
            new CreateControlManualEvidenceRequest()
              .setEvidencePayloadList([newEvidence])
              .setControlIdsList([controlIdString]),
          )
        } else if (requestId) {
          const resp = await evidenceClient.createRequestManualEvidence(
            new CreateRequestManualEvidenceRequest()
              .setEvidencePayloadList([newEvidence])
              .setRequestIdsList([requestIdString]),
          )
          await invalidateEvidence()
          return resp.getEvidenceGroupsList()[0]?.getContentId() ?? ''
        } else {
          await evidenceClient.createOrphanedManualEvidence(
            new CreateOrphanedManualEvidenceRequest().setEvidencePayloadsList([
              newEvidence,
            ]),
          )
        }
        await invalidateEvidence()
        return ''
      } catch (error) {
        showInfoToast('Evidence upload failed')
        return ''
      }
    },
    [evidenceClient, invalidateEvidence, controlId, requestId],
  )
}
